import React, { useEffect, useState } from 'react';
import { FormControlLabel, FormHelperText, Switch } from '@material-ui/core';

import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import SearchFilterButton from '../../../common/SearchFilterButton';
import SearchFilterDrawer, {
  BaseSearchFilterProps
} from '../../../common/SearchFilterDrawer';
import MultiUserSelector from '../../users/components/MultiUserSelector';
import HazardEntityStatusFilter from './HazardEntityStatusFilter';

export interface HazardFilterOptions {
  author: number[];
  assignee: number[];
  status: string[];
  assignedForReviewOnly?: boolean;
}

function createInitialOptions(
  options?: Partial<HazardFilterOptions>
): HazardFilterOptions {
  return { author: [], assignee: [], status: [], ...options };
}

export interface HazardBoardFilterProps
  extends BaseSearchFilterProps<HazardFilterOptions> {
  filterHazards?: boolean;
  options?: Partial<HazardFilterOptions>;
}

const HazardBoardFilter: React.FC<HazardBoardFilterProps> = (
  props: HazardBoardFilterProps
) => {
  const { filterHazards, options: initialOptions, onFilter } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<HazardFilterOptions>(
    createInitialOptions(initialOptions)
  );

  const updateOptions = (updatedData: Partial<HazardFilterOptions>) => {
    setOptions({ ...options, ...updatedData });
  };

  useEffect(() => {
    initialOptions && updateOptions(initialOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOptions]);

  return (
    <>
      <SearchFilterButton onClick={() => setOpen(true)} />
      <SearchFilterDrawer
        open={open}
        title='Filter Hazards'
        onClose={() => setOpen(false)}
        onClear={() => setOptions(createInitialOptions())}
        onFilter={() => {
          setOpen(false);
          onFilter?.(options);
        }}
      >
        <InlineFormControl label='Author'>
          <MultiUserSelector
            value={options.author || []}
            textFieldProps={{ label: undefined, placeholder: 'Select User' }}
            onChange={author => {
              updateOptions({ author: author.map(u => u.id) });
            }}
          />
        </InlineFormControl>
        <InlineFormControl label='Assignee'>
          <MultiUserSelector
            value={options.assignee || []}
            disabled={options.assignedForReviewOnly}
            textFieldProps={{ label: undefined, placeholder: 'Select User' }}
            onChange={assignee => {
              updateOptions({ assignee: assignee.map(u => u.id) });
            }}
          />
        </InlineFormControl>
        <InlineFormControl>
          <FormControlLabel
            label='Assigned for Review'
            control={
              <Switch
                color='primary'
                checked={options.assignedForReviewOnly || false}
                onChange={(_, checked: boolean) => {
                  const update: Partial<HazardFilterOptions> = {
                    assignedForReviewOnly: checked || undefined
                  };
                  if (checked) {
                    update.assignee = [];
                  }
                  updateOptions(update);
                }}
              />
            }
          />
          <FormHelperText>
            Toggle to show assigned for review items only.
          </FormHelperText>
        </InlineFormControl>
        <InlineFormControl label='Status'>
          <HazardEntityStatusFilter
            includeHazardStatus={filterHazards}
            value={options.status || []}
            onChange={status => updateOptions({ status: status || [] })}
          />
        </InlineFormControl>
      </SearchFilterDrawer>
    </>
  );
};

export default HazardBoardFilter;
